import { useState, useRef } from 'react';
import { useExtractEntities, useUploadDocument, useInferRelationships } from '../api/analysis';
import { useCreateEntity } from '../api/entities';
import type { AnalysisResult, ExtractedEntity, ExtractedRelationship, EntityType } from '../types';
import { useBrowserAI } from '../hooks/useBrowserAI';
import { useGPU } from '../hooks/useGPU';

const TYPE_BADGES: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
  ip_address: 'bg-red-500/20 text-red-400',
  domain: 'bg-orange-500/20 text-orange-400',
  email: 'bg-teal-500/20 text-teal-400',
  phone_number: 'bg-cyan-500/20 text-cyan-400',
};

export default function AnalysisWorkbench() {
  const [text, setText] = useState('');
  const [activeTab, setActiveTab] = useState<'text' | 'upload'>('text');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<EntityType[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [useLocalAnalysis, setUseLocalAnalysis] = useState(false);
  const [localAnalysisRunning, setLocalAnalysisRunning] = useState(false);

  const extractMut = useExtractEntities();
  const uploadMut = useUploadDocument();
  const inferMut = useInferRelationships();
  const createEntityMut = useCreateEntity();
  const [savedEntities, setSavedEntities] = useState<Set<number>>(new Set());
  const [allSaved, setAllSaved] = useState(false);

  const { runNER, analyzeSentiment, isInitialized: aiReady, loadingProgress, loadingModel, isGPUAccelerated } = useBrowserAI();
  const { backend: gpuBackend } = useGPU();

  const isProcessing = extractMut.isPending || uploadMut.isPending || localAnalysisRunning;

  function handleExtract() {
    if (!text.trim()) return;

    if (useLocalAnalysis && aiReady) {
      handleLocalExtract();
      return;
    }

    extractMut.mutate(
      {
        text,
        entity_types: selectedEntityTypes.length > 0 ? selectedEntityTypes : undefined,
      },
      {
        onSuccess: (data) => { setResult(data); setSavedEntities(new Set()); setAllSaved(false); },
      }
    );
  }

  async function handleLocalExtract() {
    if (!text.trim()) return;
    setLocalAnalysisRunning(true);

    try {
      const startTime = performance.now();
      const [entities, sentiment] = await Promise.all([
        runNER(text),
        analyzeSentiment(text),
      ]);

      const elapsed = Math.round(performance.now() - startTime);

      const mappedEntities: ExtractedEntity[] = entities.map((e, i) => ({
        name: e.text,
        type: (e.label.toLowerCase() === 'per' ? 'person' :
               e.label.toLowerCase() === 'org' ? 'organization' :
               e.label.toLowerCase() === 'loc' ? 'location' : 'document') as EntityType,
        confidence: e.score,
        span: [e.start, e.end] as [number, number],
        source_text: text.substring(Math.max(0, e.start - 20), e.end + 20),
        resolved_id: `local-${i}`,
      }));

      setResult({
        id: `local-${Date.now()}`,
        type: 'local_analysis',
        entities: mappedEntities,
        relationships: [],
        summary: `Local analysis (${gpuBackend?.toUpperCase() || 'CPU'}): found ${entities.length} entities. Sentiment: ${sentiment.label} (${Math.round(sentiment.score * 100)}%)`,
        confidence: entities.length > 0 ? entities.reduce((s, e) => s + e.score, 0) / entities.length : 0,
        processing_time_ms: elapsed,
        created_at: new Date().toISOString(),
      });
      setSavedEntities(new Set());
      setAllSaved(false);
    } catch (err) {
      console.error('[AnalysisWorkbench] Local analysis failed:', err);
    } finally {
      setLocalAnalysisRunning(false);
    }
  }

  function handleFileUpload(file: File) {
    uploadMut.mutate(file, {
      onSuccess: (data) => { setResult(data); setSavedEntities(new Set()); setAllSaved(false); },
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }

  function handleInferRelationships() {
    if (!result?.entities.length) return;
    const ids = result.entities
      .filter((e) => e.resolved_id)
      .map((e) => e.resolved_id!);
    if (ids.length < 2) return;
    inferMut.mutate(
      { entity_ids: ids },
      {
        onSuccess: (data) => setResult(data),
      }
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Analysis Workbench</h1>
        <p className="text-sm text-gray-500 mt-1">
          Extract entities and relationships from text or documents
        </p>

        {/* Local vs Server analysis toggle */}
        <div className="mt-3 flex items-center gap-3">
          <div className="flex bg-surface-tertiary rounded-lg p-0.5 w-fit">
            <button
              onClick={() => setUseLocalAnalysis(false)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                !useLocalAnalysis
                  ? 'bg-helios-700 text-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Analyse serveur
            </button>
            <button
              onClick={() => setUseLocalAnalysis(true)}
              disabled={!aiReady}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                useLocalAnalysis
                  ? 'bg-helios-700 text-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              } ${!aiReady ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              Analyse locale (GPU)
            </button>
          </div>

          {useLocalAnalysis && (
            <span className={`text-xs px-2 py-1 rounded-full border ${
              isGPUAccelerated
                ? 'bg-green-500/10 text-green-400 border-green-500/30'
                : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
            }`}>
              {gpuBackend?.toUpperCase() || 'CPU'} {isGPUAccelerated ? '(GPU)' : '(WASM)'}
            </span>
          )}

          {loadingModel && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div className="w-3 h-3 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
              <span>Loading {loadingModel}... {Math.round(loadingProgress * 100)}%</span>
              <div className="w-24 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyber-cyan rounded-full transition-all"
                  style={{ width: `${loadingProgress * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input panel */}
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex bg-surface-tertiary rounded-lg p-0.5 w-fit">
            <button
              onClick={() => setActiveTab('text')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'text'
                  ? 'bg-helios-700 text-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Text Input
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'upload'
                  ? 'bg-helios-700 text-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              File Upload
            </button>
          </div>

          {activeTab === 'text' ? (
            <div className="glass-panel p-4 space-y-4">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste text for entity extraction...&#10;&#10;Example: John Smith, CEO of Acme Corp based in New York, met with Jane Doe from Global Finance Ltd at their London office on March 15. The transaction of $2.5M was routed through account CH93-0076-2011-6238-5295-7 at Swiss National Bank."
                className="w-full h-64 p-4 bg-surface-primary border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-600 resize-none focus:outline-none focus:border-cyber-cyan/40 font-mono"
              />

              {/* Entity type filter */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Filter entity types (optional)</p>
                <div className="flex flex-wrap gap-2">
                  {(['person', 'organization', 'location', 'ip_address', 'domain', 'email', 'phone_number'] as EntityType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setSelectedEntityTypes((prev) =>
                          prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]
                        );
                      }}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        selectedEntityTypes.includes(type)
                          ? 'bg-cyber-cyan/10 text-cyber-cyan border-cyber-cyan/30'
                          : 'bg-surface-tertiary text-gray-500 border-border-subtle hover:text-gray-300'
                      }`}
                    >
                      {type.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleExtract}
                disabled={!text.trim() || isProcessing}
                className="w-full px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-cyber-cyan/20 to-helios-600/20 text-cyber-cyan border border-cyber-cyan/30 rounded-lg hover:from-cyber-cyan/30 hover:to-helios-600/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {extractMut.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                    Extracting...
                  </span>
                ) : (
                  'Extract Entities'
                )}
              </button>
            </div>
          ) : (
            <div
              className={`glass-panel p-8 text-center transition-colors ${
                dragOver ? 'border-cyber-cyan/50 bg-cyber-cyan/5' : ''
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
                accept=".pdf,.docx,.txt,.csv,.json,.xlsx"
                className="hidden"
              />

              <svg className="w-12 h-12 mx-auto text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
              </svg>
              <p className="text-sm text-gray-400 mb-2">
                Drag & drop a document or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-cyber-cyan hover:underline"
                >
                  browse files
                </button>
              </p>
              <p className="text-xs text-gray-600">PDF, DOCX, TXT, CSV, JSON, XLSX</p>

              {uploadMut.isPending && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-cyber-cyan">
                  <div className="w-4 h-4 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                  Processing document...
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {(extractMut.error || uploadMut.error) && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
              Analysis failed. Please try again.
            </div>
          )}
        </div>

        {/* Results panel */}
        <div className="space-y-4">
          {result ? (
            <>
              {/* Summary */}
              <div className="glass-panel p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-300">Analysis Results</h3>
                  <span className="text-xs text-gray-500">
                    {result.processing_time_ms}ms | Confidence: {Math.round(result.confidence * 100)}%
                  </span>
                </div>
                <p className="text-sm text-gray-400">{result.summary}</p>
              </div>

              {/* Extracted entities */}
              <div className="glass-panel p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-300">
                    Entities ({result.entities.length})
                  </h3>
                  <button
                    onClick={handleInferRelationships}
                    disabled={inferMut.isPending || result.entities.length < 2}
                    className="px-3 py-1 text-xs font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors disabled:opacity-40"
                  >
                    Infer Relationships
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {result.entities.map((entity, i) => (
                    <EntityCard
                      key={i}
                      entity={entity}
                      saved={savedEntities.has(i)}
                      onSave={async () => {
                        try {
                          await createEntityMut.mutateAsync({
                            name: entity.name,
                            entity_type: entity.type,
                            properties: {},
                            confidence: entity.confidence,
                            source: 'analysis_workbench',
                          } as Parameters<typeof createEntityMut.mutateAsync>[0]);
                          setSavedEntities(prev => new Set(prev).add(i));
                        } catch { /* ignore */ }
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Relationships */}
              {result.relationships.length > 0 && (
                <div className="glass-panel p-4">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">
                    Relationships ({result.relationships.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {result.relationships.map((rel, i) => (
                      <RelationshipCard key={i} relationship={rel} />
                    ))}
                  </div>
                </div>
              )}

              {/* Add to graph */}
              {allSaved ? (
                <div className="w-full px-4 py-2.5 text-sm font-medium text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                  {result.entities.length} entit{'\u00e9'}s sauvegard{'\u00e9'}es dans le graphe
                </div>
              ) : (
                <button
                  onClick={async () => {
                    for (let i = 0; i < result.entities.length; i++) {
                      const e = result.entities[i];
                      if (savedEntities.has(i)) continue;
                      try {
                        await createEntityMut.mutateAsync({
                          name: e.name,
                          entity_type: e.type,
                          properties: { source_text: (e as ExtractedEntity & { source_text?: string }).source_text || '' },
                          confidence: e.confidence,
                          source: 'analysis_workbench',
                        } as Parameters<typeof createEntityMut.mutateAsync>[0]);
                        setSavedEntities(prev => new Set(prev).add(i));
                      } catch { /* skip duplicates */ }
                    }
                    setAllSaved(true);
                  }}
                  disabled={createEntityMut.isPending}
                  className="w-full px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-green-500/20 to-helios-600/20 text-green-400 border border-green-500/30 rounded-lg hover:from-green-500/30 hover:to-helios-600/30 transition-all disabled:opacity-40"
                >
                  {createEntityMut.isPending
                    ? `Sauvegarde... (${savedEntities.size}/${result.entities.length})`
                    : `Ajouter tout au graphe (${result.entities.length} entit\u00e9s)`}
                </button>
              )}
            </>
          ) : (
            <div className="glass-panel p-12 flex flex-col items-center justify-center text-gray-500">
              <svg className="w-16 h-16 mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
              </svg>
              <p className="text-sm">Enter text or upload a document to begin analysis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EntityCard({ entity, onSave, saved }: { entity: ExtractedEntity; onSave?: () => void; saved?: boolean }) {
  const badgeClass = TYPE_BADGES[entity.type] || 'bg-gray-500/20 text-gray-400';

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-tertiary/50 transition-colors">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${badgeClass}`}>
        {entity.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-200 truncate">{entity.name}</p>
        <p className="text-xs text-gray-500 capitalize">{entity.type.replace('_', ' ')}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <span className="text-xs font-mono text-gray-400">
          {Math.round(entity.confidence * 100)}%
        </span>
      </div>
      <button
        onClick={onSave}
        disabled={saved}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          saved
            ? 'text-green-400 cursor-default'
            : 'hover:bg-cyber-cyan/10 text-gray-500 hover:text-cyber-cyan'
        }`}
        title={saved ? 'Saved' : 'Add to graph'}
      >
        {saved ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        )}
      </button>
    </div>
  );
}

function RelationshipCard({ relationship }: { relationship: ExtractedRelationship }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-surface-tertiary/50 text-xs">
      <span className="text-gray-200 font-medium truncate">{relationship.source}</span>
      <span className="px-2 py-0.5 bg-surface-tertiary text-gray-400 rounded-full whitespace-nowrap">
        {relationship.type.replace('_', ' ')}
      </span>
      <span className="text-gray-200 font-medium truncate">{relationship.target}</span>
      <span className="ml-auto text-gray-500 font-mono flex-shrink-0">
        {Math.round(relationship.confidence * 100)}%
      </span>
    </div>
  );
}
